<template>
    <mds-modal
        fullscreen
        action-required
        aria-label="Scenario Analysis Modal"
        :width="'1200px'"
        :class="namespaceClass()"
        v-model="showModal"
        @mds-modal-dismissed="cancelAction"
    >
        <mds-section bold border="primary" border-position="bottom">
            <template v-slot:mds-section-title>
                <span id="title-id">{{ getUILabel("SUMMARY_SCENARIO_ANALYSIS") }}</span>
            </template>
            <template v-slot:mds-section-actions>
                <mds-button-container>
                    <mds-button v-if="activeTab === 'selectScenario'" variation="secondary" type="button" @click="cancelAction">{{
                        getUILabel("COMMON_CANCEL")
                    }}</mds-button>
                    <mds-button v-if="activeTab !== 'selectScenario'" variation="secondary" type="button" @click="handlePrev">{{
                        getUILabel("COMMON_PREVIOUS")
                    }}</mds-button>
                    <mds-button variation="primary" type="button" @click="handleNext" :disabled="nextButtonDisabled">{{
                        activeTab === "scenarioAnalysis" ? getUILabel("COMMON_CLOSE") : getUILabel("COMMON_NEXT")
                    }}</mds-button>
                </mds-button-container>
            </template>
            <div v-if="activeTab === 'selectScenario'">
                <h4 :class="namespaceClass('__user-hint')">Select up to 3 to generate stress test scenarios.</h4>
                <div v-if="hypoType !== 2">
                    <mds-form>
                        <mds-fieldset variation="checkbox-group">
                            <mds-checkbox v-if="this.checkboxText.portfolio1" v-model="portfolio1" :disabled="!portfolio1 && threeSelected">{{
                                this.checkboxText.portfolio1
                            }}</mds-checkbox>
                            <mds-checkbox v-if="this.checkboxText.portfolio2" v-model="portfolio2" :disabled="!portfolio2 && threeSelected">{{
                                this.checkboxText.portfolio2
                            }}</mds-checkbox>
                            <mds-checkbox v-if="this.checkboxText.benchmark1" v-model="benchmark1" :disabled="!benchmark1 && threeSelected">{{
                                this.checkboxText.benchmark1
                            }}</mds-checkbox>
                            <mds-checkbox v-if="this.checkboxText.benchmark2" v-model="benchmark2" :disabled="!benchmark2 && threeSelected">{{
                                this.checkboxText.benchmark2
                            }}</mds-checkbox>
                        </mds-fieldset>
                    </mds-form>
                </div>
                <div v-else>
                    <mds-form>
                        <mds-fieldset variation="checkbox-group">
                            <mds-checkbox
                                v-for="item in scenarioData"
                                v-model="investmentSelection"
                                :value="item.id"
                                :checked="investmentSelection.indexOf(item.id) !== -1"
                                :disabled="investmentSelection.indexOf(item.id) === -1 && investmentSelection.length === 3"
                                :key="item.id"
                            >
                                <span v-if="item.type === 'holding'">
                                    {{ item.name }}
                                </span>
                                <span v-else> {{ item.name }} </span>
                            </mds-checkbox>
                        </mds-fieldset>
                    </mds-form>
                </div>
            </div>
            <div v-else>
                <hypo-scenario-analysis
                    v-if="reloadScenarioAnalysis"
                    mwc-id="scenarioAnalysis"
                    :environment="this.settings.environment"
                    :hypoFileType.prop="hypoType"
                    :portfolios.prop="scenarioPortfoliosData"
                    :initialInvestmentAmount.prop="scenarioPortfolioStartAmount"
                    :colors.prop="chartColors"
                    :SATokenSourceOptions.prop="SATokenSourceOptions"
                >
                </hypo-scenario-analysis>
            </div>
        </mds-section>
    </mds-modal>
</template>

<script>
import HypoVueBaseMixin from "hypo-vue-base-mixin";
import MwcSharedMixin from "@/mixins/MwcSharedMixin";
import { MdsButton, MdsButtonContainer } from "@mds/button";
import MdsModal from "@mds/modal";
import MdsForm from "@mds/form";
import MdsFieldset from "@mds/fieldset";
import MdsCheckbox from "@mds/checkbox";
import MdsSection from "@mds/section";
import "hypo-scenario-analysis/src/index";
import { getPortfolioDate, getDateString } from "@/util";

export default {
    name: "scenario-dialog",
    mixins: [HypoVueBaseMixin, MwcSharedMixin],
    components: {
        MdsModal,
        MdsSection,
        MdsButtonContainer,
        MdsButton,
        MdsForm,
        MdsFieldset,
        MdsCheckbox
    },
    props: {
        propShow: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showModal: false,
            activeTab: "selectScenario",
            portfolio1: true,
            portfolio2: true,
            benchmark2: false,
            benchmark1Status: null,
            SATokenSourceOptions: {
                refreshToken: this.refreshToken
            },
            scenarioPortfolios: [],
            reloadScenarioAnalysis: false,
            investmentSelection: [],
            scenarioData: null
        };
    },
    async mounted() {
        if (this.hypoType !== 2) {
            this.showModal = this.propShow;
        } else {
            await this.mapData();
        }
    },
    computed: {
        hypoType() {
            return parseInt(this.$store.getters["hypo/getType"]);
        },
        benchmark1: {
            get() {
                return this.benchmark1Status === null ? !this.checkboxText.portfolio2 && "benchmark1" in this.checkboxText : this.benchmark1Status;
            },
            set(val) {
                this.benchmark1Status = val;
            }
        },
        threeSelected() {
            return [this.portfolio1, this.portfolio2, this.benchmark1, this.benchmark2].filter(Boolean).length === 3;
        },
        checkboxText() {
            const result = {};
            const portfolioList = this.$store.state.investments.portfolioList;
            for (let i = 0; i < portfolioList.length; i++) {
                const showIndex = portfolioList[1] ? ` ${i + 1}` : "";
                result[`portfolio${i + 1}`] = `Portfolio${showIndex}: ${portfolioList[i].name}`;
                if (portfolioList[i].benchmark && portfolioList[i].benchmark.includeBenchmark) {
                    result[`benchmark${i + 1}`] = `Benchmark${showIndex}: ${this.getBenchmarkName(portfolioList[i].benchmark)}`;
                }
            }
            return result;
        },
        nextButtonDisabled() {
            if (!this.checkboxText.benchmark2) {
                return !this.portfolio1 && !this.benchmark1;
            }
            return [this.portfolio1, this.portfolio2, this.benchmark1, this.benchmark2].filter(Boolean).length === 0;
        },
        chartColors() {
            return this.scenarioPortfolios.map(i => i.color);
        },
        scenarioPortfoliosData() {
            return this.scenarioPortfolios.map(portfolio => {
                /* eslint-disable  no-unused-vars*/
                const { color, ...portfolioData } = portfolio;
                return portfolioData;
            });
        },
        scenarioPortfolioStartAmount() {
            const portfolioList = this.$store.state.investments.portfolioList;
            const isInvestmentFlow = this.hypoType === 2;
            if (isInvestmentFlow) {
                return portfolioList[0].securities[0].investAmount;
            }
            if (!this.portfolio1 && "portfolio2" in this.checkboxText && this.portfolio2) {
                return this.getPortfolioTotalAmount(portfolioList[1]);
            }
            return this.getPortfolioTotalAmount(portfolioList[0]);
        }
    },
    methods: {
        async mapData() {
            const { portfolioColor, benchmarkColor } = this.$store.getters["config/colorConfig"];
            const portfolioColors = portfolioColor.split(";");
            const benchmarkColors = benchmarkColor.split(";");
            const portfolioColorsLength = portfolioColors.length;
            const benchmarkColorsLength = benchmarkColors.length;

            const secs = this.$store.state.investments.portfolioList[0].securities.map(sec => {
                return {
                    name: sec.securityName,
                    identifier: sec.securityId,
                    weight: 100
                };
            });

            const portfolios = secs.map((sec, index) => {
                return {
                    id: `investment${index + 1}`,
                    type: "holding",
                    index,
                    name: sec.name,
                    color: portfolioColors[index % portfolioColorsLength],
                    holdings: [sec]
                };
            });
            const benchmarkPromises = this.$store.state.investments.portfolioList.map(async (portfolio, index) => {
                return {
                    id: `benchmark${index + 1}`,
                    type: "benchmark",
                    index: 0,
                    name: this.getBenchmarkName(portfolio.benchmark),
                    color: benchmarkColors[0 % benchmarkColorsLength],
                    holdings: await this.getBenchmarkHoldings(portfolio)
                };
            });
            const benchmarks = await Promise.all(benchmarkPromises);
            this.scenarioData = [...portfolios, ...benchmarks];
            this.showModal = this.propShow;
            const selection = this.scenarioData.length > 3 ? this.scenarioData.slice(0, 3) : [...this.scenarioData];
            this.investmentSelection = selection.map(item => item.id);
        },
        emitEvent(payload) {
            this.$emit("scenario-event", payload);
        },
        cancelAction() {
            this.reloadScenarioAnalysis = false;
            this.showModal = false;
            this.emitEvent(null);
        },
        async handleNext() {
            if (this.activeTab === "selectScenario") {
                if (this.hypoType !== 2) {
                    this.scenarioPortfolios = await this.loadScenarioPortfolios();
                } else {
                    this.scenarioPortfolios = this.scenarioData.filter(item => this.investmentSelection.indexOf(item.id) !== -1);
                }
                this.activeTab = "scenarioAnalysis";
                this.reloadScenarioAnalysis = true;
            } else if (this.activeTab === "scenarioAnalysis") {
                this.cancelAction();
            }
        },
        handlePrev() {
            this.reloadScenarioAnalysis = false;
            this.activeTab = "selectScenario";
        },
        refreshToken(callback) {
            Promise.resolve()
                .then(() => {
                    const token = window.localStorage.getItem("UIMToken");
                    if (token) {
                        return token;
                    } else {
                        return this.$fetch({
                            url: `/api/v1/auth/getcomponenttoken`,
                            method: "GET"
                        }).then(data => data.token);
                    }
                })
                .then(token => {
                    callback(null, `Bearer ${token}`);
                });
        },
        async loadScenarioPortfolios() {
            const portfolios = [];
            const portfolioList = this.$store.state.investments.portfolioList;
            const { portfolioColor, benchmarkColor } = this.$store.getters["config/colorConfig"];
            const portfolioColors = portfolioColor.split(";");
            const benchmarkColors = benchmarkColor.split(";");
            const portfolioColorsLength = portfolioColors.length;
            const benchmarkColorsLength = benchmarkColors.length;
            if ("portfolio1" in this.checkboxText && this.portfolio1) {
                portfolios.push({
                    id: "portfolio1",
                    type: "portfolio",
                    index: 0,
                    name: portfolioList[0].name,
                    color: portfolioColors[0 % portfolioColorsLength],
                    holdings: this.getPortfolioSecurities(portfolioList[0])
                });
            }
            if ("portfolio2" in this.checkboxText && this.portfolio2) {
                portfolios.push({
                    id: "portfolio2",
                    type: "portfolio",
                    index: 1,
                    name: portfolioList[1].name,
                    color: portfolioColors[1 % portfolioColorsLength],
                    holdings: this.getPortfolioSecurities(portfolioList[1])
                });
            }

            if ("benchmark1" in this.checkboxText && this.benchmark1) {
                portfolios.push({
                    id: "benchmark1",
                    type: "benchmark",
                    index: 0,
                    name: this.getBenchmarkName(portfolioList[0].benchmark),
                    color: benchmarkColors[0 % benchmarkColorsLength],
                    holdings: await this.getBenchmarkHoldings(portfolioList[0])
                });
            }

            if ("benchmark2" in this.checkboxText && this.benchmark2) {
                portfolios.push({
                    id: "benchmark2",
                    type: "benchmark",
                    index: 1,
                    name: this.getBenchmarkName(portfolioList[1].benchmark),
                    color: benchmarkColors[1 % benchmarkColorsLength],
                    holdings: await this.getBenchmarkHoldings(portfolioList[1])
                });
            }
            return portfolios;
        },
        getBenchmarkName(benchmark) {
            const { benchmarkType, benchmarkHoldings, customBenchmarkName } = benchmark;
            const benchmarkTexts = {
                0: benchmarkHoldings && benchmarkHoldings[0].securityName,
                1: this.getUILabel("BENCHMARK_AUTO_BY_ASSET_ALLOCATION"),
                2: this.getUILabel("BENCHMARK_AUTO_BY_MSTAR_CATEGORY"),
                3: customBenchmarkName ? customBenchmarkName : this.getUILabel("COMMON_BENCHMARK")
            };
            return benchmarkTexts[benchmarkType];
        },
        getPortfolioSecurities(portfolio) {
            const totalAmount = this.getPortfolioTotalAmount(portfolio);
            return portfolio.securities.map(security => {
                return {
                    identifier: security.securityId,
                    weight: (security.investAmount / totalAmount) * 100
                };
            });
        },
        getPortfolioTotalAmount(portfolio) {
            return portfolio.securities.reduce((prev, security) => {
                return prev + security.investAmount;
            }, 0);
        },
        async getBenchmarkHoldings(portfolio) {
            const assetGroups = this.$store.state.hypo.assetAllocation;
            const { endDate } = getPortfolioDate(portfolio);
            const { benchmarkType, benchmarkHoldings, customBenchmarkId } = portfolio.benchmark;
            const actions = {
                0: async () => benchmarkHoldings.map(holding => ({ ...holding, weight: 100 })),
                1: async () => this.getAutoBenchmarkHoldings(benchmarkType, portfolio, assetGroups),
                2: async () => this.getAutoBenchmarkHoldings(benchmarkType, portfolio, assetGroups),
                3: async () =>
                    this.getCustomBenchmarkHoldings(customBenchmarkId, {
                        enddate: getDateString(endDate)
                    })
            };
            return actions[benchmarkType]().then(securities => {
                return securities.map(security => {
                    return {
                        identifier: security.securityId,
                        weight: security.weight
                    };
                });
            });
        },
        async getCustomBenchmarkHoldings(benchmarkId, params) {
            const searchParams = new URLSearchParams(params);
            return this.$fetch({
                url: `/api/v1/benchmark/custombenchmark/${benchmarkId}?${searchParams.toString()}`,
                method: "GET"
            });
        },
        async getAutoBenchmarkHoldings(benchmarkType, portfolio, assetGroups) {
            return this.$fetch({
                url: `/api/v1/benchmark/autobenchmark/${benchmarkType}`,
                method: "POST",
                body: JSON.stringify({
                    portfolio: portfolio,
                    assetGroups: assetGroups
                })
            });
        }
    },
    watch: {
        propShow: {
            handler(newVal) {
                this.showModal = newVal;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import "@mds/typography";

.scenario-dialog {
    ::v-deep .mds-section__content___hypo {
        height: calc(100vh - 65px);
        overflow-x: hidden;
        overflow-y: auto;
    }

    &__user-hint {
        @include mds-level-5-heading();
        margin-top: 0;
        margin-bottom: $mds-space-2-x;
    }
}
</style>
